/**
 * Qué parte de la deuda es EXIGIBLE hoy y qué parte está por venir.
 *
 * El sistema puede cobrar meses del ciclo siguiente por anticipado, así que un
 * expediente casi siempre tiene cargos con vencimiento futuro. Contarlos como
 * deuda tenía dos consecuencias, y las dos son errores de fondo:
 *
 *   - La pantalla decía que un estudiante debía Q1,465 cuando en realidad estaba
 *     al corriente y lo único vivo eran dos mensualidades de enero y febrero del
 *     año siguiente, que todavía no se le han pedido a nadie.
 *   - El reporte de estudiantes al día salía VACÍO, porque bastaba con tener
 *     seedeado un mes del ciclo próximo para dejar de estar al día.
 *
 * La regla: un cargo es exigible si su vencimiento cae **en el mes en curso o
 * antes**. El corte es el mes y no el día a propósito. La mensualidad de agosto
 * vence el 31 de agosto, y el 22 de agosto ya se está cobrando en ventanilla: con
 * un corte diario habría desaparecido del saldo justo el mes que toca pagar.
 *
 * Lo que NO cambia es la validación del cobro. Pagar por adelantado sigue siendo
 * legítimo —«si desean pagar 2027 pueden hacerlo»— así que el límite de lo que se
 * admite en una transacción sigue siendo la deuda TOTAL. Exigible es un concepto
 * de presentación y de reporte, no un permiso.
 */

/** Último día del mes al que pertenece la fecha operativa dada (`AAAA-MM-DD`). */
export function finDelMes(fecha: string): string {
  const anio = Number(fecha.slice(0, 4));
  const mes = Number(fecha.slice(5, 7));
  // Día 0 del mes siguiente es el último del actual, y `Date.UTC` evita que la zona
  // horaria mueva el resultado un día en cualquiera de los dos sentidos.
  const ultimo = new Date(Date.UTC(anio, mes, 0));
  return ultimo.toISOString().slice(0, 10);
}

/**
 * `true` si un cargo con ese vencimiento ya se le puede exigir al encargado.
 *
 * Un cargo SIN fecha de vencimiento se considera exigible: es el caso de la
 * inscripción y del material, que se deben desde que se generan.
 */
export function esExigible(vencimiento: string | null, hoy: string): boolean {
  if (vencimiento === null) return true;
  return vencimiento <= finDelMes(hoy);
}

export interface RepartoDeuda {
  /** Lo que ya se le puede exigir al encargado. Es `vencido + del mes`. */
  exigibleMinor: number;
  /** Cargos de meses posteriores: se pueden pagar por adelantado, no se deben. */
  porVenirMinor: number;
  /**
   * Parte de lo exigible cuyo plazo YA pasó.
   *
   * Se separa porque no es lo mismo deber la mensualidad del mes en curso que
   * arrastrar la inscripción desde enero, y una sola cifra las confunde. Con el
   * padre de familia delante, el cajero necesita poder decir cuánto viene de atrás.
   */
  vencidoMinor: number;
  /** Parte de lo exigible que vence este mes y todavía está en plazo. */
  delMesMinor: number;
}

/** Separa una lista de cargos pendientes en exigible y por venir. */
export function repartirDeuda(
  cargos: readonly { dueDate: string | null; pendienteMinor: number }[],
  hoy: string,
): RepartoDeuda {
  let vencidoMinor = 0;
  let delMesMinor = 0;
  let porVenirMinor = 0;

  for (const cargo of cargos) {
    if (cargo.pendienteMinor <= 0) continue;
    if (!esExigible(cargo.dueDate, hoy)) {
      porVenirMinor += cargo.pendienteMinor;
      continue;
    }
    // Sin fecha cuenta como vencido: se debe desde que se generó.
    if (cargo.dueDate === null || cargo.dueDate < hoy)
      vencidoMinor += cargo.pendienteMinor;
    else delMesMinor += cargo.pendienteMinor;
  }

  return {
    exigibleMinor: vencidoMinor + delMesMinor,
    porVenirMinor,
    vencidoMinor,
    delMesMinor,
  };
}
